import { MusicGenerationTaskType } from '@prisma/client'
import { z } from 'zod'

import { templateFieldValuesSchema } from './app/generation/template'

export enum PromptMode {
  SMART = 'smart',
  CUSTOM = 'custom',
  QUICK = 'quick',
  TEMPLATE = 'template',
}

export enum StyleMode {
  PROMPT = 'prompt',
  REFERENCE = 'reference',
}

export const advancedSchema = z
  .object({
    vocalGender: z.enum(['m', 'f']).optional(),
    styleWeight: z.number().min(0).max(1).optional(),
    creativeWeight: z.number().min(0).max(1).optional(),
    audioWeight: z.number().min(0).max(1).optional(),
  })
  .optional()

const noAdvancedSchema = z.never().optional()

const commonTextInputSchema = z
  .object({
    prompt: z.string(),
    imageUrl: z.string().nullish(),
  })
  .or(
    z.object({
      imageUrl: z.string(),
    }),
  )

const commonTemplateInputSchema = z.object({
  templateUuid: z.string(),
  templateValues: templateFieldValuesSchema,
})

const commonTaskInputSchema = z
  .object({
    taskType: z.literal(MusicGenerationTaskType.Song),
    lyrics: z.string().min(1).max(3000),
  })
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Instrumental),
    }),
  )

const commonPromptInputSchema = z.object({
  styleMode: z.literal(StyleMode.PROMPT),
  labelsSummary: z.string().nullish(),
  labels: z.object({
    genre: z.string().min(1).max(64).nullish(),
    mood: z.string().min(1).max(64).nullish(),
    voice: z.string().min(1).max(64).nullish(),
    description: z.string().min(1).max(1000),
  }),
})

export const commonV3QuickGenerationConfigSchema = z
  .object({
    taskType: z.enum([
      MusicGenerationTaskType.Song,
      MusicGenerationTaskType.Instrumental,
    ]),
    promptMode: z.literal(PromptMode.QUICK),
    styleMode: z.literal(StyleMode.PROMPT).default(StyleMode.PROMPT),
    advanced: noAdvancedSchema,
  })
  .and(commonTextInputSchema)

export const commonV3SmartGenerationConfigSchema = z
  .object({
    promptMode: z.literal(PromptMode.SMART),
    advanced: noAdvancedSchema,
  })
  .and(commonPromptInputSchema)
  .and(commonTaskInputSchema)
  .and(commonTextInputSchema)

export const commonV3CustomGenerationConfigSchema = z
  .object({
    taskType: z.literal(MusicGenerationTaskType.Song),
    promptMode: z.literal(PromptMode.CUSTOM),
    styleMode: z.literal(StyleMode.PROMPT),
    lyrics: z.string(),
    labelsSummary: z.string().nullish(),
    advanced: advancedSchema,
    labels: z
      .object({
        genre: z.string().min(1).max(64).nullish(),
        mood: z.string().min(1).max(64).nullish(),
        voice: z.string().min(1).max(64).nullish(),
        description: z.string().min(1).max(1000),
      })
      .nullish(),
  })
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Song),
      promptMode: z.literal(PromptMode.CUSTOM),
      styleMode: z.literal(StyleMode.REFERENCE),
      lyrics: z.string(),
      songReference: z.string().nullish(),
      vocalReference: z.string().nullish(),
      advanced: noAdvancedSchema,
    }),
  )
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Song),
      promptMode: z.literal(PromptMode.CUSTOM),
      styleMode: z.literal(StyleMode.REFERENCE),
      lyrics: z.string(),
      songUrl: z.string().nullish(),
      vocalUrl: z.string().nullish(),
      advanced: noAdvancedSchema,
    }),
  )
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Instrumental),
      promptMode: z.literal(PromptMode.CUSTOM),
      styleMode: z.literal(StyleMode.PROMPT),
      labelsSummary: z.string().nullish(),
      advanced: advancedSchema,
      labels: z.object({
        genre: z.string().min(1).max(64).nullish(),
        mood: z.string().min(1).max(64).nullish(),
        voice: z.string().min(1).max(64).nullish(),
        description: z.string().min(1).max(1000),
      }),
    }),
  )
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Instrumental),
      promptMode: z.literal(PromptMode.CUSTOM),
      styleMode: z.literal(StyleMode.REFERENCE),
      instrumentalReference: z.string(),
      advanced: noAdvancedSchema,
    }),
  )
  .or(
    z.object({
      taskType: z.literal(MusicGenerationTaskType.Instrumental),
      promptMode: z.literal(PromptMode.CUSTOM),
      styleMode: z.literal(StyleMode.REFERENCE),
      instrumentalUrl: z.string(),
      advanced: noAdvancedSchema,
    }),
  )

export const commonV3TemplateGenerationConfigSchema = z
  .object({
    promptMode: z.literal(PromptMode.TEMPLATE),
    advanced: noAdvancedSchema,
  })
  .and(commonPromptInputSchema)
  .and(commonTaskInputSchema)
  .and(commonTemplateInputSchema)

export const commonTaskGenerationConfigSchema = z
  .union([
    commonV3QuickGenerationConfigSchema,
    commonV3SmartGenerationConfigSchema,
    commonV3CustomGenerationConfigSchema,
    commonV3TemplateGenerationConfigSchema,
  ])
  .and(
    z.object({
      similarTaskId: z.string().optional(),
    }),
  )

export type CommonTaskGenerationConfig = z.infer<
  typeof commonTaskGenerationConfigSchema
>

export const createSimilarTaskInputSchema = z
  .object({
    promptMode: z.nativeEnum(PromptMode),
    templateUuid: z.string().nullish(),
    templateValues: templateFieldValuesSchema.nullish(),
    prompt: z.string().nullish(),
    imageUrl: z.string().nullish(),
    labelsSummary: z.string().nullish(),
    labels: z
      .object({
        genre: z.string().min(1).max(64).nullish(),
        mood: z.string().min(1).max(64).nullish(),
        voice: z.string().min(1).max(64).nullish(),
        description: z.string().min(1).max(1000).nullish(),
      })
      .nullish(),
    similarTaskId: z.string(),
  })
  .and(
    z
      .object({
        taskType: z.literal(MusicGenerationTaskType.Song),
        lyrics: z.string(),
        styleMode: z.literal(StyleMode.REFERENCE),
        songUrl: z.string(),
      })
      .or(
        z.object({
          taskType: z.literal(MusicGenerationTaskType.Instrumental),
          styleMode: z.literal(StyleMode.REFERENCE),
          instrumentalUrl: z.string(),
        }),
      )
      .or(commonTaskInputSchema.and(commonPromptInputSchema)),
  )
